import dayjs from 'utils/dayjs';
import { groupBy } from '../../../helpers/utils';
import { parseInteger } from '../../../helpers/parsingHelper';
import { getDatesArrayForRange } from '../../../helpers/dateHelper';
import { isEmpty } from 'lodash';

let ParsingUtils;
const API_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Extract an hotel's pricing from a row
 * @param  {object} pricingObject - a row from the API
 * @return {object} an object with the price, currency, message, and is it hasPricing
 */
export const getPricing = (pricingObject) => {
    if (!pricingObject) {
        return {
            hasPricing: false,
            price: 0,
            currency: null,
            message: null,
        };
    }
    const hasPricing = !(pricingObject.value === 0 && pricingObject.shopCurrency === '');
    const msg = pricingObject.message;
    const currency = pricingObject.shopCurrency;
    return {
        hasPricing,
        price: parseInteger(pricingObject.value) * 100,
        currency: !currency || currency === '' ? null : currency,
        message: !msg || msg === '' ? null : msg,
    };
};

/**
 * Extract today's & yesterday's pricing for an hotel, for all dates in the range
 * @param  {object[]} hotelRows - all rows from APi corresponding to an hotel
 * @param  {moment[]} dates     - the list of dates to consider
 * @return {object} an object with the prices for each date
 */
const getPricingByHotel = (hotelRows, dates) => {
    const groupedByDay = groupBy(hotelRows, 'stayDate');
    return dates.map((date) => {
        const match = groupedByDay[date.format(API_DATE_FORMAT)];
        const object = {
            date: date.format('YYYY-MM-DD'),
        };

        object.todayPricing = ParsingUtils.getPricing(match && match.find((row) => row.period === 'current'));
        object.yesterdayPricing = ParsingUtils.getPricing(match && match.find((row) => row.period === 'previous'));
        return object;
    });
};

/**
 * Parses the OTA Insights data into an array of entries for each competitor
 * @param  {object} params
 * @param  {string} params.startDate - insights starting date
 * @param  {string} params.endDate - the ending date of the data
 * @param  {object[]} params.payload - the data from the API
 * @return {object[]} An array with 1 entry per hotel, and its price as of today/yesterday for each date
 *          { hotelId, hotelName, pricings: [{ yesterday, today}] }
 *          with yesterday/today as { value, currency }
 */
export const parseOtaData = ({ date, payload }) => {
    if (isEmpty(payload)) {
        return null;
    }
    const dates = getDatesArrayForRange(
        dayjs(date, API_DATE_FORMAT).subtract(1, 'day'),
        dayjs(date, API_DATE_FORMAT).add(1, 'day'),
    );
    const groupedByHotel = groupBy(payload, 'hotelId');
    const records = Object.values(groupedByHotel).map((hotelRows) => ({
        hotelId: hotelRows[0].hotelId,
        hotelName: hotelRows[0].hotelName,
        pricings: ParsingUtils.getPricingByHotel(hotelRows, dates),
    }));
    const { datesList, demand } = dates.reduce(
        (accu, dateObj) => {
            const dateStr = dateObj.format('YYYY-MM-DD');
            const currentMatch = payload.find((row) => row.stayDate === dateStr && row.period === 'current');
            const previousMatch = payload.find((row) => row.stayDate === dateStr && row.period === 'previous');
            return {
                demand: {
                    ...accu.demand,
                    [dateStr]: {
                        today: currentMatch ? currentMatch.demand || null : null,
                        yesterday: previousMatch ? previousMatch.demand || null : null,
                    },
                },
                datesList: [...accu.datesList, dateStr],
            };
        },
        { datesList: [], demand: {} },
    );
    return {
        dates: datesList,
        records,
        demand,
    };
};

export default ParsingUtils = {
    getPricing,
    getPricingByHotel,
    parseOtaData,
};
